import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSignOutAlt, FaDog, FaStore, FaUsers, FaHeart } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [token, setToken] = useState('');
  const [data, setData] = useState({
    venues: [],
    dogs: [],
    partners: [],
    applications: [],
    sponsorships: [],
    bowlPurchases: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    setToken(adminToken);
    fetchData(adminToken);
  }, [navigate]);

  const fetchData = async (adminToken) => {
    try {
      const headers = { Authorization: `Bearer ${adminToken}` };
      
      const [venues, dogs, partners, applications, sponsorships, bowlPurchases] = await Promise.all([
        axios.get(`${API}/venues`),
        axios.get(`${API}/dogs`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/admin/host-applications`, { headers }),
        axios.get(`${API}/admin/sponsorships`, { headers }),
        axios.get(`${API}/admin/bowl-purchases`, { headers })
      ]);

      setData({
        venues: venues.data,
        dogs: dogs.data,
        partners: partners.data,
        applications: applications.data,
        sponsorships: sponsorships.data,
        bowlPurchases: bowlPurchases.data
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const stats = [
    { label: 'Total Venues', value: data.venues.length, icon: FaStore, color: 'bg-blue-500' },
    { label: 'Dogs in Care', value: data.dogs.length, icon: FaDog, color: 'bg-emerald-500' },
    { label: 'Partners', value: data.partners.length, icon: FaUsers, color: 'bg-purple-500' },
    { label: 'Sponsorships', value: data.sponsorships.length, icon: FaHeart, color: 'bg-pink-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">SmartPaw Care Management</p>
          </div>
          <div className="flex gap-4">
            <a
              href="/"
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              View Site
            </a>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              data-testid="logout-button"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                  <stat.icon className="text-2xl text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {['overview', 'venues', 'dogs', 'partners', 'applications'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-4 text-sm font-medium capitalize ${
                    activeTab === tab
                      ? 'border-b-2 border-emerald-600 text-emerald-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`${tab}-tab`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Applications</h3>
                  {data.applications.length === 0 ? (
                    <p className="text-gray-500">No applications yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {data.applications.slice(0, 5).map((app) => (
                        <div key={app.id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-gray-900">{app.venue_name}</p>
                              <p className="text-sm text-gray-600">{app.contact_name} • {app.contact_email}</p>
                            </div>
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                              {app.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Sponsorships</h3>
                  {data.sponsorships.length === 0 ? (
                    <p className="text-gray-500">No sponsorships yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {data.sponsorships.slice(0, 5).map((sponsor) => (
                        <div key={sponsor.id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-gray-900">{sponsor.dog_name}</p>
                              <p className="text-sm text-gray-600">
                                {sponsor.donor_name || 'Anonymous'} • ₾{sponsor.amount}/{sponsor.frequency}
                              </p>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(sponsor.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'venues' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">All Venues ({data.venues.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.venues.map((venue) => (
                    <div key={venue.id} className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold text-gray-900">{venue.name}</h4>
                      <p className="text-sm text-gray-600">{venue.address}</p>
                      <div className="mt-2 flex gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {venue.venue_type}
                        </span>
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs">
                          {venue.district}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'dogs' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">All Dogs ({data.dogs.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.dogs.map((dog) => (
                    <div key={dog.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex gap-4">
                        <img
                          src={dog.image_url}
                          alt={dog.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{dog.name}</h4>
                          <p className="text-sm text-gray-600">{dog.age} • {dog.breed}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            {dog.care_venue_name || 'No venue assigned'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'partners' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">All Partners ({data.partners.length})</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {data.partners.map((partner) => (
                    <div key={partner.id} className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="font-semibold text-gray-900">{partner.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'applications' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Host Applications ({data.applications.length})
                </h3>
                <div className="space-y-4">
                  {data.applications.map((app) => (
                    <div key={app.id} className="p-6 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">{app.venue_name}</h4>
                          <p className="text-sm text-gray-600">{app.venue_type} • {app.district}</p>
                        </div>
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                          {app.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Contact</p>
                          <p className="text-sm text-gray-600">{app.contact_name}</p>
                          <p className="text-sm text-gray-600">{app.contact_email}</p>
                          <p className="text-sm text-gray-600">{app.contact_phone}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Address</p>
                          <p className="text-sm text-gray-600">{app.address}</p>
                        </div>
                      </div>
                      {app.message && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Message</p>
                          <p className="text-sm text-gray-600">{app.message}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
